/**
 * @file section-form-context.ts
 * @description Общее для полей редактора секции (phase19.16 Р2): замечания
 * проверки по пути (`validateSectionPayload` до отправки и 422 сервера),
 * предупреждения о текстах (§16.6), подсветка на канве того, что меняет
 * поле под курсором (`space-highlight`), и вид сайта (цвета ролей).
 * Поля читают это через `useSectionForm`, а не цепочкой свойств.
 *
 * phase19.16 Р2а: контекст секции (обычная страница, место, шаблон товара —
 * какие элементы можно поставить), где в шаблоне уже стоят элементы данных
 * товара («Уже есть в блоке «…» [Показать]») и перенос элемента в другой
 * блок.
 *
 * SB122 (ревью партии): пути всех замечаний (`issuePaths`) — для ✕ в
 * заголовке свёрнутой группы; `reveal` — запрос «раскрыть первую группу с
 * ошибкой и дать фокус полю» после неудачного «Сохранить блок» (номер
 * попытки; видимая панель выполняет его и сбрасывает `revealDone`).
 *
 * SB120 этап 1 (uiux-sb120-stage1 §4, §6; §7.19 фазы «а где исправлять?»):
 * `preview(path, value)` — живой предпросмотр на канве (`useCanvasPreview`);
 * `lookWarnings` — предупреждения об оформлении (`sectionLookWarnings`,
 * не блокируют); `mediaSizes` — размеры файлов секции; `revealPath` —
 * переход из списка замечаний к конкретному полю (раскрыть его группу).
 *
 * SB120 этап 3а (uiux-sb120-stage3 §2, §4; db-design-sb120-stage3 §6):
 * `elementWarnings` — предупреждения об элементах (`sectionElementWarnings`:
 * контраст, мелкий H1, кнопка ниже 48, больше 3 шрифтов, надпись без
 * плашки, удалённый свой шрифт) — «!» / «✕» у группы «Цвет и шрифт» и
 * строки в списке замечаний; публикацию и сохранение не блокируют.
 */

import type { ProductElementKind, SectionContext, SectionElementWarning, SectionLookWarning, SectionMediaSizes, SiteTheme } from "@chaiberi/shared";
import { createContext, useContext } from "react";

import type { SiteTextWarning } from "../../../lib/api-site-pages";
import type { ElementRef } from "../../../lib/site-builder/template-rules";
import type { SpaceHighlightPart } from "../../../lib/site-view/canvas-messages";

export interface SectionFormContextValue {
  /** Замечание проверки по пути `columns.items.0.elements.2.label`. */
  issueAt: (path: string) => string | null;
  /** Есть ли замечания внутри пути (для отметки группы или элемента). */
  hasIssuesUnder: (prefix: string) => boolean;
  /** Пути всех замечаний (группа с ошибкой — по реестру `issue-groups.ts`). */
  issuePaths: readonly string[];
  /** Запрос показать замечания (номер неудачного сохранения); `null` — нет. */
  reveal: number | null;
  /** Запрос выполнен видимой панелью. */
  revealDone: () => void;
  /** Переход к полю из списка замечаний: раскрыть группу этого пути (иначе — первую с ошибкой). */
  revealPath: string | null;
  /** Живой предпросмотр значения на канве (не запись). */
  preview: (path: string, value: unknown) => void;
  /** Предупреждения об оформлении (не блокируют). */
  lookWarnings: readonly SectionLookWarning[];
  /** Предупреждения об элементах (не блокируют). */
  elementWarnings: readonly SectionElementWarning[];
  /** Размеры файлов секции (по id медиатеки). */
  mediaSizes: SectionMediaSizes;
  /** Предупреждения о текстах по точному пути. */
  warningsAt: (path: string) => SiteTextWarning[];
  /** Курсор над настройкой отступа/рисунков — подсветить на канве; `null` — снять. */
  highlight: (part: SpaceHighlightPart | null) => void;
  /** Оформление черновика сайта (цвета ролей для образцов). */
  theme: SiteTheme | null;
  /** Где секция: обычная страница, место или шаблон страницы товара. */
  context: SectionContext;
  /** Шаблон товара: где уже стоит элемент данных товара (в этом или другом блоке). */
  taken: Partial<Record<ProductElementKind, ElementRef>>;
  /** Показать элемент (выбрать его блок и элемент). */
  showElement: (ref: ElementRef) => void;
  /** Открыть «Перенести в другой блок» для элемента; `null` — переносить некуда. */
  moveElement: ((elementId: string) => void) | null;
}

const NOOP: SectionFormContextValue = {
  issueAt: () => null,
  hasIssuesUnder: () => false,
  issuePaths: [],
  reveal: null,
  revealDone: () => undefined,
  revealPath: null,
  preview: () => undefined,
  lookWarnings: [],
  elementWarnings: [],
  mediaSizes: {},
  warningsAt: () => [],
  highlight: () => undefined,
  theme: null,
  context: "page",
  taken: {},
  showElement: () => undefined,
  moveElement: null,
};

export const SectionFormContext = createContext<SectionFormContextValue>(NOOP);

export function useSectionForm(): SectionFormContextValue {
  return useContext(SectionFormContext);
}

/** Контекст из списка замечаний и предупреждений. */
export function makeSectionFormValue(
  issues: ReadonlyArray<{ path: string; message: string }>,
  warnings: readonly SiteTextWarning[],
  highlight: SectionFormContextValue["highlight"],
  theme: SiteTheme | null,
  extra: Partial<
    Pick<
      SectionFormContextValue,
      "context" | "taken" | "showElement" | "moveElement" | "reveal" | "revealDone" | "revealPath" | "preview" | "lookWarnings" | "elementWarnings" | "mediaSizes"
    >
  > = {},
): SectionFormContextValue {
  return {
    ...NOOP,
    issueAt: (path) => issues.find((i) => i.path === path)?.message ?? null,
    hasIssuesUnder: (prefix) => issues.some((i) => i.path === prefix || i.path.startsWith(`${prefix}.`)),
    issuePaths: issues.map((i) => i.path),
    warningsAt: (path) => warnings.filter((w) => w.path === path),
    highlight,
    theme,
    ...extra,
  };
}
